<template>
    <div class="auth-artist-register-container">
        <div class="titles">
            <p class="title-white">Let's set up your</p>
            <p class="title-yellow">Artist Profile</p>
        </div>
        <div class="form-wrapper">
            <div class="band-name-input-wrapper">
                <label for="band-name">Artist Name*</label>
                <input
                    v-model="$v.firstFormValues.band_name.$model"
                    v-autofocus
                    :class="{
                        'error-input':
                            $v.firstFormValues.band_name.$dirty &&
                            !$v.firstFormValues.band_name.required
                    }"
                    @blur="$v.firstFormValues.band_name.$touch"
                    type="text"
                    placeholder="The name you perform under"
                />
            </div>
            <div class="home-location-input-wrapper">
                <label for="home-location">Where are you based? *</label>
                <div class="location-inputs">
                    <input
                        v-model="firstFormValues.city"
                        :class="{
                            'error-input':
                                $v.firstFormValues.city.$dirty &&
                                !$v.firstFormValues.city.required
                        }"
                        @blur="$v.firstFormValues.city.$touch"
                        type="text"
                        placeholder="City"
                    />
                    <div
                        @click="handleToggleDropdown"
                        @keydown.space="handleToggleDropdown"
                        @keydown.enter="handleToggleDropdown"
                        class="state-select"
                        :class="{ 'error-input': missingStateValue }"
                        tabindex="0"
                    >
                        <span v-if="firstFormValues.state" class="state">
                            {{ firstFormValues.state }}
                        </span>
                        <span v-else>State</span>
                        <img
                            :src="dropdownOpen ? URLS.icons.vectorUp : URLS.icons.vectorDown"
                            alt="vector"
                        />

                        <div
                            ref="dropdown"
                            v-if="dropdownOpen"
                            class="state-options"
                            tabindex="0"
                        >
                            <p
                                v-for="state in USstates"
                                :key="state[1]"
                                @click="handleSelectState(state[0])"
                                @keyup="handleSelectState(state[0])"
                                class="state-option"
                                tabindex="0"
                            >
                                {{ state[0] }}
                            </p>
                        </div>
                    </div>
                    <input
                        v-model="firstFormValues.zip"
                        :class="{
                            'error-input':
                                $v.firstFormValues.zip.$dirty &&
                                !$v.firstFormValues.zip.required
                        }"
                        @blur="$v.firstFormValues.zip.$touch"
                        type="text"
                        placeholder="Zip"
                    />
                </div>
            </div>
            <div class="social-link-wrapper">
                <auth-link-social role="artist">
                    <template #message>
                        <p>Links & Social Media</p>
                        <p>
                            We'll need at least one social linked to make your profile public.
                        </p>
                    </template>
                </auth-link-social>
            </div>
        </div>
        <button @click="handleFirstFormContinue($v)" class="continue-btn">Continue</button>
        <button @click="handleFirstFormBack" class="back-btn">Back</button>
    </div>
</template>
<script>
    import { useArtistRegister } from '@/composables/auth/useArtistRegister'
    import AuthLinkSocial from '@/components/auth/parts/AuthLinkSocial.vue'
    import { required } from 'vuelidate/lib/validators'

    export default {
        name: 'AuthArtistRegisterFirstStep',
        components: { AuthLinkSocial },
        validations: {
            firstFormValues: {
                band_name: { required },
                city: { required },
                zip: { required }
            }
        },
        setup() {
            const {
                URLS,
                USstates,
                dropdown,
                dropdownOpen,
                missingStateValue,
                firstFormValues,
                handleToggleDropdown,
                handleSelectState,
                handleFirstFormContinue,
                handleFirstFormBack
            } = useArtistRegister()

            return {
                URLS,
                USstates,
                dropdown,
                dropdownOpen,
                missingStateValue,
                firstFormValues,
                handleToggleDropdown,
                handleSelectState,
                handleFirstFormContinue,
                handleFirstFormBack
            }
        }
    }
</script>
<style lang="scss" scoped>
    .auth-artist-register-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 40px;

        p {
            margin-bottom: 0px;
        }
        button {
            border: none;
        }

        .titles {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            width: 100%;

            p {
                font-family: Instrument Sans;
                font-size: 38px;
                font-style: normal;
                font-weight: 500;
                line-height: 110%;
                letter-spacing: -0.76px;
            }
            .title-white {
                color: #dedede;
            }
            .title-yellow {
                color: #cdeb09;
            }
        }

        .form-wrapper {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 24px;

            label {
                color: #8b8b8b;
                font-family: Instrument Sans;
                font-size: 12px;
                font-style: normal;
                font-weight: 650;
                line-height: 135%;
                letter-spacing: 0.12px;
            }
            input {
                padding: 12px 16px 16px 16px;
                border-radius: 12px;
                border: 1px solid #8b8b8b;
                width: 100%;
                color: #dedede;
                background: inherit;
                font-family: Instrument Sans;
                font-size: 16px;
                font-style: normal;
                font-weight: 550;
                line-height: 125%;
            }
            input::placeholder {
                overflow: hidden;
                color: #646464;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-family: Instrument Sans;
                font-size: 16px;
                font-style: normal;
                font-weight: 550;
                line-height: 125%;
            }
            .error-input {
                border-color: #ed3c0d !important;
            }
            .state-select {
                cursor: pointer;
                position: relative;
                padding: 12px 16px 16px 16px;
                border-radius: 12px;
                border: 1px solid #8b8b8b;
                width: 100%;
                background: inherit;
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;

                span,
                .state {
                    overflow: hidden;
                    color: #646464;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    font-family: Instrument Sans;
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 550;
                    line-height: 125%;
                    width: 89%;
                }
                .state {
                    color: #ebebeb;
                }

                .state-options::-webkit-scrollbar {
                    width: 8px;
                }
                .state-options::-webkit-scrollbar-track {
                    background-color: none;
                }
                .state-options::-webkit-scrollbar-thumb {
                    max-height: 64px;
                    border-radius: 8px;
                    background: #8b8b8b;
                }
                .state-options {
                    position: absolute;
                    top: 115%;
                    right: 1%;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                    align-items: flex-start;
                    gap: 16px;
                    max-width: 100%;
                    max-height: 200px;
                    overflow-y: scroll;
                    overflow-x: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    padding: 12px 12px 16px 16px;
                    border-radius: 7px;
                    border: 1px solid #8b8b8b;
                    background: #262626;

                    .state-option {
                        cursor: pointer;
                        color: #646464;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 550;
                        line-height: 125%;
                        width: 100%;
                    }
                }
            }

            .band-name-input-wrapper,
            .home-location-input-wrapper {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-end;
                gap: 6px;
            }

            .home-location-input-wrapper {
                .location-inputs {
                    display: flex;
                    flex-direction: row;
                    gap: 12px;
                }
            }
            .social-link-wrapper {
                width: 100%;

                p {
                    color: #8b8b8b;
                    font-family: Instrument Sans;
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 650;
                    line-height: 135%;
                    letter-spacing: 0.12px;
                }
            }
        }
        .continue-btn {
            padding: 11px 30px 12px 30px;
            border-radius: 100px;
            background: #cdeb09;
            color: black;
            font-family: Instrument Sans;
            font-size: 18px;
            font-style: normal;
            font-weight: 550;
            line-height: 120%;
            width: 100%;
        }
        .back-btn {
            color: #8b8b8b;
            text-align: center;
            font-family: Instrument Sans;
            font-size: 16px;
            font-style: normal;
            font-weight: 550;
            line-height: 125%;
            text-decoration-line: underline;
            background: none;
        }
    }
</style>
